import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  BarChartOutlined,
  CodeOutlined,
  DeleteOutlined,
  EditOutlined,
  FileSearchOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  RollbackOutlined,
  StopOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { MenuProps, UploadProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { adminAPI } from '../services/api';
import type {
  AdminModelListResponse,
  AdminModelMeta,
  ModelActivationPreflightResponse,
  ModelActivationRequest,
  ModelCandidatePackage,
  ModelQualificationRun,
  ModelRelease,
  ModelValidationEvidenceResponse,
} from '../types';
import { formatErrorMessage } from '../utils/errorHandler';
import ModelQualificationStatus from './ModelQualificationStatus';
import ModelScriptUpgradeModal from './ModelScriptUpgradeModal';
import ModelPackageLifecycle, {
  isCurrentCandidateActive,
  resolveModelPackageLifecycle,
  type ModelPackageLifecycleAction,
} from './ModelPackageLifecycle';
import {
  CompactStatusGlyph,
  CompactStatusLegend,
  resolveCompactStatusTone,
} from './CompactStatus';

const { Text } = Typography;

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const asModelRows = (
  value: AdminModelListResponse | AdminModelMeta[],
): AdminModelMeta[] => Array.isArray(value) ? value : value.models;

const manifestModelId = (candidate: ModelCandidatePackage) => (
  typeof candidate.manifest.model_id === 'string'
    ? candidate.manifest.model_id
    : undefined
);

const sourceQualityTier = (candidate: ModelCandidatePackage) => {
  const gate = isRecord(candidate.manifest.quality_gate)
    ? candidate.manifest.quality_gate
    : undefined;
  return String(gate?.tier || '-');
};

const sourceSha256 = (candidate: ModelCandidatePackage) => {
  const source = isRecord(candidate.manifest.source)
    ? candidate.manifest.source
    : undefined;
  return typeof source?.sha256 === 'string' ? source.sha256 : '-';
};

export const matchesCandidateKeyword = (
  candidate: ModelCandidatePackage,
  meta: AdminModelMeta | undefined,
  rawKeyword: string,
) => {
  const keyword = rawKeyword.trim().toLocaleLowerCase();
  if (!keyword) return true;
  return [
    candidate.candidate_id,
    candidate.model_id,
    manifestModelId(candidate),
    candidate.package_version,
    candidate.package_sha256,
    meta?.display_name,
    meta?.drug_name,
    meta?.drug_label,
    meta?.first_author,
    meta?.publication_year,
  ].map((value) => String(value || '').toLocaleLowerCase())
    .some((value) => value.includes(keyword));
};

const QUALIFIED_STATUSES = new Set(['platform_qualified', 'clinical_validated']);

const isQualifiedStatus = (status?: string) => QUALIFIED_STATUSES.has(String(status || ''));

const draftMissingItems = (meta?: AdminModelMeta) => {
  if (!meta) return [];
  const missing: string[] = [];
  if (!String(meta.first_author || '').trim()) missing.push('第一作者');
  if (!meta.publication_year) missing.push('发表年份');
  if (!String(meta.drug_name || '').trim() || meta.drug_name === 'pending') missing.push('药物名称');
  return missing;
};

const activationErrorMessage = (error: unknown) => {
  if (!isRecord(error)) return undefined;
  const response = isRecord(error.response) ? error.response : undefined;
  const data = response && isRecord(response.data) ? response.data : undefined;
  const detail = data?.detail;
  if (isRecord(detail) && typeof detail.message === 'string') return detail.message;
  return undefined;
};

const evidenceText = (value: Record<string, unknown>) => {
  const entries = Object.entries(value);
  if (!entries.length) return '-';
  return entries.map(([key, item]) => `${key}: ${String(item)}`).join('; ');
};

const summaryField = (summary: Record<string, unknown> | undefined, key: string) => {
  const value = summary?.[key];
  return value === null || value === undefined ? '-' : String(value);
};

interface OverrideFormValues {
  reason: string;
  expires_at: Dayjs;
  evidence_mode: 'run' | 'no_run';
  no_run_statement?: string;
}

interface ReviewFormValues {
  review_status: 'approved' | 'rejected';
  review_note?: string;
}

interface RenameFormValues {
  display_name: string;
}

interface ModelPackageGroup {
  key: string;
  modelId?: string;
  representative: ModelCandidatePackage;
  versions: ModelCandidatePackage[];
}

export interface CandidatePackagesTabProps {
  onOpenClinicalResults?: (modelId: string, runId: string) => void;
  onEditModel?: (modelId: string) => void;
  refreshToken?: number;
}

const CandidatePackagesTab: React.FC<CandidatePackagesTabProps> = ({
  onOpenClinicalResults,
  onEditModel,
  refreshToken,
}) => {
  const [packages, setPackages] = useState<ModelCandidatePackage[]>([]);
  const [modelMeta, setModelMeta] = useState<Record<string, AdminModelMeta>>({});
  const [runsByPackage, setRunsByPackage] = useState<Record<string, ModelQualificationRun[]>>({});
  const [releasesByModel, setReleasesByModel] = useState<Record<string, ModelRelease[]>>({});
  const [evidenceByModel, setEvidenceByModel] = useState<Record<string, ModelValidationEvidenceResponse>>({});
  const [preflightByModel, setPreflightByModel] = useState<Record<string, ModelActivationPreflightResponse>>({});
  const [detailPackageId, setDetailPackageId] = useState<string>();
  const [candidateKeyword, setCandidateKeyword] = useState('');
  const [packageStatusFilter, setPackageStatusFilter] = useState<string>();
  const [workflowFilter, setWorkflowFilter] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState<string>();
  const [qualifyingPackageId, setQualifyingPackageId] = useState<string>();
  const [associatingPackageId, setAssociatingPackageId] = useState<string>();
  const [creatingDraftPackageId, setCreatingDraftPackageId] = useState<string>();
  const [publishingModelId, setPublishingModelId] = useState<string>();
  const [activatingModelId, setActivatingModelId] = useState<string>();
  const [switchingReleaseId, setSwitchingReleaseId] = useState<string>();
  const [upgradeCandidate, setUpgradeCandidate] = useState<ModelCandidatePackage>();
  const [baseLoadNotice, setBaseLoadNotice] = useState<string>();
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [overrideCandidate, setOverrideCandidate] = useState<ModelCandidatePackage>();
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideForm] = Form.useForm<OverrideFormValues>();
  const [reviewCandidate, setReviewCandidate] = useState<ModelCandidatePackage>();
  const [reviewQualificationRunId, setReviewQualificationRunId] = useState<string>();
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewForm] = Form.useForm<ReviewFormValues>();
  const [renameCandidate, setRenameCandidate] = useState<ModelCandidatePackage>();
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameForm] = Form.useForm<RenameFormValues>();
  const [expandedModelKeys, setExpandedModelKeys] = useState<React.Key[]>([]);
  const mountedRef = useRef(false);
  const baseRequestRef = useRef(0);
  const runRequestRef = useRef<Record<string, number>>({});
  const evidenceRequestRef = useRef<Record<string, number>>({});
  const preflightRequestRef = useRef<Record<string, number>>({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadBaseData = useCallback(async () => {
    const requestId = baseRequestRef.current + 1;
    baseRequestRef.current = requestId;
    setLoading(true);
    try {
      const [packageResult, modelResult] = await Promise.allSettled([
        adminAPI.listModelCandidatePackages(),
        adminAPI.listModelsMeta(),
      ]);
      if (!mountedRef.current || baseRequestRef.current !== requestId) return;

      const loadIssues: string[] = [];
      if (packageResult.status === 'fulfilled') {
        setPackages(packageResult.value.items);
      } else {
        setPackages([]);
        loadIssues.push(formatErrorMessage(packageResult.reason, '模型包加载失败'));
      }

      if (modelResult.status === 'fulfilled') {
        setModelMeta(Object.fromEntries(
          asModelRows(modelResult.value).map((item) => [item.model_id, item]),
        ));
      } else {
        loadIssues.push(formatErrorMessage(modelResult.reason, '模型资料加载失败'));
      }
      setBaseLoadNotice(loadIssues.length ? loadIssues.join('；') : undefined);
      if (loadIssues.length) message.warning(`模型包工作台部分数据未加载：${loadIssues.join('；')}`);
    } finally {
      if (mountedRef.current && baseRequestRef.current === requestId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBaseData();
  }, [loadBaseData, refreshToken]);

  const loadRuns = useCallback(async (packageId: string, force = false, silent = false) => {
    if (!force && runsByPackage[packageId]) return runsByPackage[packageId];
    const requestId = (runRequestRef.current[packageId] || 0) + 1;
    runRequestRef.current[packageId] = requestId;
    setLoadingRuns(packageId);
    try {
      const response = await adminAPI.listModelQualificationRuns(packageId);
      if (mountedRef.current && runRequestRef.current[packageId] === requestId) {
        setRunsByPackage((previous) => ({ ...previous, [packageId]: response.items }));
      }
      return response.items;
    } catch (error) {
      if (mountedRef.current && !silent) message.error(formatErrorMessage(error, '加载资格运行失败'));
      return [];
    } finally {
      if (mountedRef.current && runRequestRef.current[packageId] === requestId) {
        setLoadingRuns(undefined);
      }
    }
  }, [runsByPackage]);

  const loadReleases = useCallback(async (modelId: string, silent = false) => {
    try {
      const response = await adminAPI.listModelReleases(modelId);
      if (mountedRef.current) {
        setReleasesByModel((previous) => ({ ...previous, [modelId]: response.items }));
      }
      return response.items;
    } catch (error) {
      if (mountedRef.current && !silent) message.error(formatErrorMessage(error, '加载模型版本历史失败'));
      return [];
    }
  }, []);

  const loadClinicalEvidence = useCallback(async (modelId: string, force = false, silent = false) => {
    if (!force && evidenceByModel[modelId]) return evidenceByModel[modelId];
    const requestId = (evidenceRequestRef.current[modelId] || 0) + 1;
    evidenceRequestRef.current[modelId] = requestId;
    try {
      const response = await adminAPI.getModelValidationEvidence(modelId);
      if (mountedRef.current && evidenceRequestRef.current[modelId] === requestId) {
        setEvidenceByModel((previous) => ({ ...previous, [modelId]: response }));
      }
      return response;
    } catch (error) {
      if (mountedRef.current && !silent) message.error(formatErrorMessage(error, '加载临床验证证据失败'));
      return undefined;
    }
  }, [evidenceByModel]);

  const loadActivationPreflight = useCallback(async (
    modelId: string,
    request: ModelActivationRequest = {},
  ) => {
    const requestId = (preflightRequestRef.current[modelId] || 0) + 1;
    preflightRequestRef.current[modelId] = requestId;
    const response = await adminAPI.preflightModelActivation(modelId, request);
    if (mountedRef.current && preflightRequestRef.current[modelId] === requestId) {
      setPreflightByModel((previous) => ({ ...previous, [modelId]: response }));
    }
    return response;
  }, []);

  const reportCandidateError = (
    candidate: ModelCandidatePackage,
    error: unknown,
    fallback: string,
  ) => {
    const text = formatErrorMessage(error, fallback);
    setRowErrors((previous) => ({ ...previous, [candidate.id]: text }));
    message.error(text);
    return text;
  };

  const showDetails = async (candidate: ModelCandidatePackage) => {
    setDetailPackageId(candidate.id);
    setRowErrors((previous) => ({ ...previous, [candidate.id]: '' }));
    const results = await Promise.allSettled([
      loadRuns(candidate.id, false, true),
      candidate.model_id ? loadClinicalEvidence(candidate.model_id, false, true) : Promise.resolve(undefined),
      candidate.model_id ? loadActivationPreflight(candidate.model_id) : Promise.resolve(undefined),
      candidate.model_id ? loadReleases(candidate.model_id, true) : Promise.resolve([]),
    ]);
    if (results.some((result) => result.status === 'rejected')) {
      setRowErrors((previous) => ({
        ...previous,
        [candidate.id]: '资格详情已部分加载，激活预检暂时不可用，请稍后刷新',
      }));
    }
  };

  const handleUpload: UploadProps['beforeUpload'] = async (file) => {
    try {
      const result = await adminAPI.uploadModelCandidatePackage(file as File);
      message.success(result.reused ? '模型包已存在，已复用现有记录' : '模型包上传成功');
      await loadBaseData();
      setDetailPackageId(result.id);
      await loadRuns(result.id, true);
    } catch (error) {
      message.error(formatErrorMessage(error, '模型包上传失败'));
    }
    return false;
  };

  const openScriptUpgrade = (candidate: ModelCandidatePackage) => {
    setUpgradeCandidate(candidate);
  };

  const handleRemovePackage = async (candidate: ModelCandidatePackage) => {
    const runs = await loadRuns(candidate.id, true);
    const preservesEvidence = runs.length > 0;
    Modal.confirm({
      title: preservesEvidence ? '撤回模型版本' : '删除模型包草稿',
      content: preservesEvidence
        ? '该模型包已有不可变验证记录，将保留证据并标记为已撤回，之后不能继续验证或发布。'
        : '该模型包尚无验证记录，将永久删除数据库记录和模型文件。',
      okText: preservesEvidence ? '确认撤回' : '确认删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          if (preservesEvidence) {
            await adminAPI.withdrawModelCandidatePackage(candidate.id);
            message.success('模型版本已撤回，验证证据已保留');
          } else {
            await adminAPI.deleteModelCandidatePackage(candidate.id);
            message.success('模型包草稿已删除');
          }
          if (detailPackageId === candidate.id) setDetailPackageId(undefined);
          await loadBaseData();
        } catch (error) {
          reportCandidateError(candidate, error, preservesEvidence ? '模型版本撤回失败' : '模型包删除失败');
          throw error;
        }
      },
    });
  };

  const openRename = (candidate: ModelCandidatePackage) => {
    if (!candidate.model_id) return;
    renameForm.setFieldsValue({
      display_name: modelMeta[candidate.model_id]?.display_name || candidate.model_id,
    });
    setRenameCandidate(candidate);
  };

  const submitRename = async () => {
    if (!renameCandidate?.model_id) return;
    const values = await renameForm.validateFields();
    setRenameSubmitting(true);
    try {
      await adminAPI.updateModelDisplayName(renameCandidate.model_id, values.display_name.trim());
      message.success('模型展示名称已更新；技术 ID 和历史关联保持不变');
      await loadBaseData();
      setRenameCandidate(undefined);
    } catch (error) {
      reportCandidateError(renameCandidate, error, '模型展示名称更新失败');
    } finally {
      setRenameSubmitting(false);
    }
  };

  const handleSelectRelease = (candidate: ModelCandidatePackage, release: ModelRelease) => {
    if (!candidate.model_id || release.is_active) return;
    Modal.confirm({
      title: '切换模型版本',
      content: `确认切换到版本 ${release.version}？当前模型启停状态不变，运行时脚本将原子切换。`,
      okText: '确认切换',
      cancelText: '取消',
      onOk: async () => {
        setSwitchingReleaseId(release.id);
        try {
          await adminAPI.selectModelRelease(candidate.model_id!, release.id);
          message.success(`已切换到模型版本 ${release.version}`);
          await Promise.all([loadBaseData(), loadReleases(candidate.model_id!)]);
        } catch (error) {
          reportCandidateError(candidate, error, '模型版本切换失败');
          throw error;
        } finally {
          setSwitchingReleaseId(undefined);
        }
      },
    });
  };

  const handleAssociate = async (candidate: ModelCandidatePackage, modelId: string) => {
    setAssociatingPackageId(candidate.id);
    setRowErrors((previous) => ({ ...previous, [candidate.id]: '' }));
    try {
      await adminAPI.associateModelCandidatePackage(candidate.id, modelId);
      message.success(`模型包已关联模型 ${modelId}`);
      await loadBaseData();
    } catch (error) {
      reportCandidateError(candidate, error, '模型包关联失败');
    } finally {
      setAssociatingPackageId(undefined);
    }
  };

  const handleCreateDraft = async (candidate: ModelCandidatePackage) => {
    setCreatingDraftPackageId(candidate.id);
    setRowErrors((previous) => ({ ...previous, [candidate.id]: '' }));
    try {
      const result = await adminAPI.createModelDraftFromCandidatePackage(candidate.id);
      const modelId = result.model_id || manifestModelId(candidate);
      if (modelId) {
        setModelMeta((previous) => ({
          ...previous,
          [modelId]: previous[modelId] || {
            model_id: modelId,
            model_status: 'inactive',
            review_status: 'pending',
            qualification_status: 'not_started',
            validation_status: 'pending',
            draft_mode: true,
          },
        }));
      }
      message.success(`模型草稿 ${modelId || ''} 已创建并关联，正在打开模型向导`);
      await loadBaseData();
      if (modelId) onEditModel?.(modelId);
    } catch (error) {
      reportCandidateError(candidate, error, '从模型包创建模型草稿失败');
    } finally {
      setCreatingDraftPackageId(undefined);
    }
  };

  const handleQualify = async (candidate: ModelCandidatePackage) => {
    if (candidate.status === 'qualification_running') return;
    if (!candidate.model_id) {
      reportCandidateError(candidate, '请先创建或关联模型', '请先创建或关联模型');
      return;
    }
    if (modelMeta[candidate.model_id]?.draft_mode) {
      reportCandidateError(candidate, '请先完善并提交模型草稿', '请先完善并提交模型草稿');
      return;
    }
    setQualifyingPackageId(candidate.id);
    setRowErrors((previous) => ({ ...previous, [candidate.id]: '' }));
    try {
      await adminAPI.qualifyModelCandidatePackage(candidate.id);
      message.success('资格验证运行完成');
      const refreshResults = await Promise.allSettled([
        loadBaseData(),
        loadRuns(candidate.id, true),
        candidate.model_id
          ? loadActivationPreflight(candidate.model_id)
          : Promise.resolve(undefined),
      ]);
      if (refreshResults.some((result) => result.status === 'rejected')) {
        setRowErrors((previous) => ({
          ...previous,
          [candidate.id]: '资格验证已完成，但激活预检刷新失败，请刷新页面',
        }));
      }
    } catch (error) {
      reportCandidateError(candidate, error, '资格验证运行失败');
    } finally {
      setQualifyingPackageId(undefined);
    }
  };

  const handleActivate = async (
    candidate: ModelCandidatePackage,
    runId?: string,
    requestOverride?: ModelActivationRequest['override'],
  ) => {
    if (!candidate.model_id) return false;
    const request: ModelActivationRequest = {
      ...(runId ? { qualification_run_id: runId } : {}),
      ...(requestOverride ? { override: requestOverride } : {}),
    };
    setActivatingModelId(candidate.model_id);
    setRowErrors((previous) => ({ ...previous, [candidate.id]: '' }));
    try {
      await adminAPI.activateModel(candidate.model_id, request);
      message.success(`模型 ${candidate.model_id} 已批准全局测试`);
      const refreshResults = await Promise.allSettled([
        loadBaseData(),
        loadActivationPreflight(candidate.model_id),
      ]);
      if (refreshResults.some((result) => result.status === 'rejected')) {
        setRowErrors((previous) => ({
          ...previous,
          [candidate.id]: '模型已批准全局测试，但状态刷新失败，请刷新页面确认',
        }));
      }
      return true;
    } catch (error) {
      const exactMessage = activationErrorMessage(error)
        || formatErrorMessage(error, '批准全局测试失败');
      setRowErrors((previous) => ({ ...previous, [candidate.id]: exactMessage }));
      message.error(exactMessage);
      return false;
    } finally {
      setActivatingModelId(undefined);
    }
  };

  const handlePublish = async (candidate: ModelCandidatePackage, runId?: string) => {
    if (!candidate.model_id) return false;
    setPublishingModelId(candidate.model_id);
    setRowErrors((previous) => ({ ...previous, [candidate.id]: '' }));
    try {
      const result = await adminAPI.publishQualifiedModel(candidate.model_id, {
        ...(runId ? { qualification_run_id: runId } : {}),
      });
      message.success(result.already_published
        ? `模型 ${candidate.model_id} 当前脚本包版本已在模型库中`
        : `模型 ${candidate.model_id} 新版本已发布，现有启停状态保持不变`);
      await Promise.all([loadBaseData(), loadReleases(candidate.model_id)]);
      const preflight = await loadActivationPreflight(candidate.model_id);
      const releaseId = typeof result.promotion.release_id === 'string'
        ? result.promotion.release_id
        : undefined;
      const alreadyActive = Boolean(
        releaseId && modelMeta[candidate.model_id]?.active_release_id === releaseId,
      );
      if (preflight.decision.allowed && !alreadyActive) {
        const switching = modelMeta[candidate.model_id]?.model_status === 'active';
        Modal.confirm({
          title: switching ? '新版本已发布，立即切换？' : '批准进入全局测试？',
          content: switching
            ? '旧版本仍在运行。确认后将原子切换到刚发布的版本。'
            : '批准后该版本将参与全局模型匹配；临床证据状态保持独立展示。',
          okText: switching ? '立即切换' : '批准全局测试',
          cancelText: '稍后处理',
          onOk: () => handleActivate(
            candidate,
            preflight.qualification_run_id || runId,
          ),
        });
      }
      return true;
    } catch (error) {
      reportCandidateError(candidate, error, '发布模型失败');
      return false;
    } finally {
      setPublishingModelId(undefined);
    }
  };

  const openReview = async (candidate: ModelCandidatePackage) => {
    if (!candidate.model_id) return;
    const meta = modelMeta[candidate.model_id];
    reviewForm.setFieldsValue({
      review_status: meta?.review_status === 'rejected' ? 'rejected' : 'approved',
      review_note: meta?.review_note || undefined,
    });
    setReviewQualificationRunId(undefined);
    setReviewCandidate(candidate);
    const runs = await loadRuns(candidate.id, true);
    const latestQualifiedRun = runs.find((run) => ['platform_qualified', 'clinical_validated'].includes(run.status));
    if (!latestQualifiedRun) {
      reportCandidateError(candidate, '当前模型包尚无通过技术资格的运行记录', '暂不能审核');
      setReviewCandidate(undefined);
      return;
    }
    setReviewQualificationRunId(latestQualifiedRun.id);
  };

  const submitReview = async () => {
    if (!reviewCandidate?.model_id || !reviewQualificationRunId) return;
    const values = await reviewForm.validateFields();
    setReviewSubmitting(true);
    try {
      await adminAPI.reviewModel(reviewCandidate.model_id, {
        review_status: values.review_status,
        review_note: String(values.review_note || '').trim() || undefined,
        qualification_run_id: reviewQualificationRunId,
      });
      message.success(values.review_status === 'approved' ? '模型审核已通过' : '模型已驳回');
      await loadBaseData();
      await loadActivationPreflight(reviewCandidate.model_id).catch(() => undefined);
      setReviewCandidate(undefined);
      setReviewQualificationRunId(undefined);
    } catch (error) {
      reportCandidateError(reviewCandidate, error, '模型审核失败');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const openOverride = (candidate: ModelCandidatePackage) => {
    const latestRun = (runsByPackage[candidate.id] || [])[0];
    overrideForm.setFieldsValue({
      reason: '',
      expires_at: dayjs().add(1, 'hour'),
      evidence_mode: latestRun ? 'run' : 'no_run',
      no_run_statement: latestRun ? undefined : 'No qualification run is available for this model.',
    });
    setOverrideCandidate(candidate);
  };

  const submitOverride = async () => {
    if (!overrideCandidate?.model_id) return;
    const values = await overrideForm.validateFields();
    const latestRun = (runsByPackage[overrideCandidate.id] || [])[0];
    const override = {
      reason: values.reason.trim(),
      expires_at: values.expires_at.toISOString(),
      ...(values.evidence_mode === 'run' && latestRun
        ? { qualification_run_id: latestRun.id }
        : { no_run_statement: String(values.no_run_statement || '').trim() }),
    };
    const request: ModelActivationRequest = { override };
    setOverrideSubmitting(true);
    try {
      const preflight = await loadActivationPreflight(overrideCandidate.model_id, request);
      if (!preflight.decision.allowed) {
        setRowErrors((previous) => ({
          ...previous,
          [overrideCandidate.id]: preflight.decision.message,
        }));
        return;
      }
      const activated = await handleActivate(
        overrideCandidate,
        preflight.qualification_run_id || undefined,
        override,
      );
      if (activated) setOverrideCandidate(undefined);
    } catch (error) {
      setRowErrors((previous) => ({
        ...previous,
        [overrideCandidate.id]: formatErrorMessage(error, '审核覆盖预检失败'),
      }));
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const latestCandidateIdByModel = useMemo(() => {
    const latest = new Map<string, ModelCandidatePackage>();
    packages.forEach((candidatePackage) => {
      if (!candidatePackage.model_id || ['withdrawn', 'archived'].includes(candidatePackage.status)) return;
      const current = latest.get(candidatePackage.model_id);
      const candidateTime = dayjs(candidatePackage.created_at || candidatePackage.updated_at || 0).valueOf();
      const currentTime = dayjs(current?.created_at || current?.updated_at || 0).valueOf();
      if (!current || candidateTime > currentTime) latest.set(candidatePackage.model_id, candidatePackage);
    });
    return Object.fromEntries(
      Array.from(latest.entries()).map(([modelId, candidatePackage]) => [modelId, candidatePackage.id]),
    );
  }, [packages]);

  const isLatestCandidate = (candidate: ModelCandidatePackage) => (
    !candidate.model_id || latestCandidateIdByModel[candidate.model_id] === candidate.id
  );

  const candidateWorkflowBucket = useCallback((candidate: ModelCandidatePackage) => {
    if (['withdrawn', 'archived'].includes(candidate.status)) return 'historical';
    const expectedModelId = manifestModelId(candidate);
    const meta = candidate.model_id ? modelMeta[candidate.model_id] : undefined;
    const state = resolveModelPackageLifecycle({
      candidate,
      meta,
      expectedModelExists: Boolean(expectedModelId && modelMeta[expectedModelId]),
      isLatestCandidate: !candidate.model_id || latestCandidateIdByModel[candidate.model_id] === candidate.id,
      currentReleaseActive: isCurrentCandidateActive(candidate, meta),
      draftMissingItems: draftMissingItems(meta),
      workflow: meta?.workflow,
    });
    if (state.route === 'historical' || state.action === 'view') return 'historical';
    if (['associate', 'create_draft', 'edit_model'].includes(String(state.action || ''))) return 'registration';
    if (state.action === 'qualify' || (state.action === 'refresh' && candidate.status === 'qualification_running')) {
      return 'qualification';
    }
    if (state.action === 'review') return 'review';
    if (state.action === 'publish') return 'publication';
    if (state.action === 'activate' || state.action === 'override') return 'activation';
    if (state.action === 'refresh') return 'registration';
    return 'complete';
  }, [latestCandidateIdByModel, modelMeta]);

  const packageStatusOptions = useMemo(() => Array.from(new Set(
    packages.map((candidatePackage) => candidatePackage.status),
  )).sort().map((value) => ({ value, label: value })), [packages]);

  const filteredPackages = useMemo(() => {
    return packages.filter((candidatePackage) => {
      const meta = candidatePackage.model_id ? modelMeta[candidatePackage.model_id] : undefined;
      if (!matchesCandidateKeyword(candidatePackage, meta, candidateKeyword)) return false;
      if (packageStatusFilter && candidatePackage.status !== packageStatusFilter) return false;
      if (workflowFilter && candidateWorkflowBucket(candidatePackage) !== workflowFilter) return false;
      return true;
    });
  }, [candidateKeyword, candidateWorkflowBucket, modelMeta, packageStatusFilter, packages, workflowFilter]);

  const lifecycleLoading = (candidate: ModelCandidatePackage) => Boolean(
    associatingPackageId === candidate.id
    || creatingDraftPackageId === candidate.id
    || qualifyingPackageId === candidate.id
    || publishingModelId === candidate.model_id
    || activatingModelId === candidate.model_id
    || reviewSubmitting && reviewCandidate?.id === candidate.id,
  );

  const handleLifecycleAction = async (
    candidate: ModelCandidatePackage,
    action: ModelPackageLifecycleAction,
  ) => {
    const expectedModelId = manifestModelId(candidate);
    if (action === 'associate' && expectedModelId) {
      await handleAssociate(candidate, expectedModelId);
      return;
    }
    if (action === 'create_draft') {
      await handleCreateDraft(candidate);
      return;
    }
    if (action === 'edit_model') {
      if (candidate.model_id && onEditModel) onEditModel(candidate.model_id);
      else await showDetails(candidate);
      return;
    }
    if (action === 'qualify') {
      await handleQualify(candidate);
      return;
    }
    if (action === 'review') {
      void openReview(candidate);
      return;
    }
    if (action === 'publish') {
      const runs = await loadRuns(candidate.id, true);
      await handlePublish(candidate, runs[0]?.id);
      return;
    }
    if (action === 'activate' && candidate.model_id) {
      Modal.confirm({
        title: modelMeta[candidate.model_id]?.model_status === 'active'
          ? '切换全局测试版本？'
          : '批准进入全局测试？',
        content: '技术资格、版本审核和发布已完成。批准后模型将参与全局匹配；临床证据不作为本次阻断条件。',
        okText: modelMeta[candidate.model_id]?.model_status === 'active' ? '确认切换' : '批准全局测试',
        cancelText: '取消',
        onOk: async () => {
          try {
            const preflight = await loadActivationPreflight(candidate.model_id!);
            if (preflight.decision.allowed) {
              await handleActivate(candidate, preflight.qualification_run_id || undefined);
            } else {
              setRowErrors((previous) => ({
                ...previous,
                [candidate.id]: preflight.decision.message,
              }));
              await showDetails(candidate);
            }
          } catch (error) {
            reportCandidateError(candidate, error, '全局测试预检失败');
          }
        },
      });
      return;
    }
    if (action === 'override') {
      openOverride(candidate);
      return;
    }
    if (action === 'refresh') {
      await Promise.allSettled([
        loadBaseData(),
        loadRuns(candidate.id, true),
        candidate.model_id
          ? loadActivationPreflight(candidate.model_id)
          : Promise.resolve(undefined),
      ]);
      return;
    }
    await showDetails(candidate);
  };

  const columns = [
    {
      title: '模型包 / 模型',
      key: 'identity',
      width: 260,
      render: (_: unknown, candidate: ModelCandidatePackage) => (
        <Space direction="vertical" size={2}>
          <Text strong>{candidate.candidate_id}</Text>
          <Text type="secondary">
            {candidate.model_id
              ? `${modelMeta[candidate.model_id]?.display_name || candidate.model_id} · ${candidate.model_id}`
              : '未关联模型'}
          </Text>
          {rowErrors[candidate.id] ? (
            <Text type="danger" ellipsis={{ tooltip: rowErrors[candidate.id] }} style={{ maxWidth: 240 }}>
              {rowErrors[candidate.id]}
            </Text>
          ) : null}
        </Space>
      ),
    },
    { title: '版本', dataIndex: 'package_version', width: 110 },
    {
      title: '来源质量',
      width: 110,
      align: 'center' as const,
      render: (_: unknown, candidate: ModelCandidatePackage) => {
        const tier = sourceQualityTier(candidate);
        return <CompactStatusGlyph label="来源质量" value={tier} tone={tier === 'pass' ? 'success' : 'warning'} />;
      },
    },
    {
      title: '包状态',
      dataIndex: 'status',
      width: 96,
      align: 'center' as const,
      render: (status: string) => (
        <CompactStatusGlyph label="模型包" value={status} tone={resolveCompactStatusTone(status)} />
      ),
    },
    {
      title: '包哈希',
      dataIndex: 'package_sha256',
      ellipsis: true,
      render: (hash: string) => <Text code>{hash}</Text>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 360,
      render: (_: unknown, candidate: ModelCandidatePackage) => {
        const expectedModelId = manifestModelId(candidate);
        const meta = candidate.model_id ? modelMeta[candidate.model_id] : undefined;
        const isWithdrawn = ['withdrawn', 'archived'].includes(candidate.status);
        const latest = isLatestCandidate(candidate);
        const secondaryItems: MenuProps['items'] = candidate.model_id && latest && !isWithdrawn
          ? [
            {
              key: 'rename',
              icon: <EditOutlined />,
              label: '修改展示名称',
            },
            {
              key: 'script-upgrade',
              icon: <CodeOutlined />,
              label: '脚本快速升级',
              disabled: Boolean(meta?.draft_mode),
            },
            ...(QUALIFIED_STATUSES.has(candidate.status) ? [{
              key: 'rerun',
              icon: <PlayCircleOutlined />,
              label: '重新运行资格验证',
            }] : []),
            { type: 'divider' as const },
            {
              key: 'remove',
              icon: (runsByPackage[candidate.id] || []).length ? <StopOutlined /> : <DeleteOutlined />,
              label: (runsByPackage[candidate.id] || []).length ? '撤回版本' : '移除模型包',
              danger: true,
              disabled: candidate.status === 'qualification_running',
            },
          ]
          : [];
        const handleSecondaryAction: MenuProps['onClick'] = ({ key }) => {
          if (key === 'rename') openRename(candidate);
          if (key === 'script-upgrade') openScriptUpgrade(candidate);
          if (key === 'rerun') void handleQualify(candidate);
          if (key === 'remove') void handleRemovePackage(candidate);
        };
        return (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <ModelPackageLifecycle
              compact
              candidate={candidate}
              meta={meta}
              expectedModelExists={Boolean(expectedModelId && modelMeta[expectedModelId])}
              isLatestCandidate={latest}
              currentReleaseActive={isCurrentCandidateActive(candidate, meta)}
              draftMissingItems={draftMissingItems(meta)}
              workflow={meta?.workflow}
              loading={lifecycleLoading(candidate)}
              onAction={(action) => void handleLifecycleAction(candidate, action)}
            />
            <Space size={4} wrap>
              <Button
                size="small"
                type="link"
                icon={<FileSearchOutlined />}
                aria-label="Qualification details"
                onClick={() => void showDetails(candidate)}
              >
                查看流程与证据
              </Button>
              {secondaryItems.length ? (
                <Dropdown
                  menu={{ items: secondaryItems, onClick: handleSecondaryAction }}
                  trigger={['click']}
                >
                  <Button size="small" type="text" icon={<MoreOutlined />} aria-label="More candidate actions">
                    更多
                  </Button>
                </Dropdown>
              ) : null}
            </Space>
          </Space>
        );
      },
    },
  ];

  const groupedPackages = useMemo<ModelPackageGroup[]>(() => {
    const groups = new Map<string, ModelPackageGroup>();
    filteredPackages.forEach((candidate) => {
      const key = candidate.model_id || manifestModelId(candidate) || `unassociated:${candidate.id}`;
      const existing = groups.get(key);
      if (existing) {
        existing.versions.push(candidate);
        const currentTime = dayjs(existing.representative.created_at || existing.representative.updated_at || 0).valueOf();
        const candidateTime = dayjs(candidate.created_at || candidate.updated_at || 0).valueOf();
        if (candidateTime > currentTime) existing.representative = candidate;
      } else {
        groups.set(key, {
          key,
          modelId: candidate.model_id || manifestModelId(candidate) || undefined,
          representative: candidate,
          versions: [candidate],
        });
      }
    });
    return Array.from(groups.values()).map((group) => ({
      ...group,
      versions: [...group.versions].sort((a, b) => (
        dayjs(b.created_at || b.updated_at || 0).valueOf()
        - dayjs(a.created_at || a.updated_at || 0).valueOf()
      )),
    }));
  }, [filteredPackages]);

  const groupedColumns = [
    {
      title: '模型',
      key: 'model',
      width: 300,
      render: (_: unknown, group: ModelPackageGroup) => {
        const candidate = group.representative;
        const meta = group.modelId ? modelMeta[group.modelId] : undefined;
        return (
          <Space direction="vertical" size={2}>
            <Text strong>{meta?.display_name || group.modelId || '未关联模型'}</Text>
            <Text type="secondary">{group.modelId || candidate.candidate_id}</Text>
          </Space>
        );
      },
    },
    {
      title: '版本数',
      key: 'version_count',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, group: ModelPackageGroup) => <Tag color={group.versions.length > 1 ? 'blue' : 'default'}>{group.versions.length}</Tag>,
    },
    {
      title: '最新版本',
      key: 'latest_version',
      width: 130,
      render: (_: unknown, group: ModelPackageGroup) => group.representative.package_version,
    },
    {
      title: '最新状态',
      key: 'latest_status',
      width: 130,
      render: (_: unknown, group: ModelPackageGroup) => (
        <CompactStatusGlyph
          label="最新状态"
          value={group.representative.status}
          tone={resolveCompactStatusTone(group.representative.status)}
        />
      ),
    },
    {
      title: '历史版本',
      key: 'history',
      render: (_: unknown, group: ModelPackageGroup) => {
        const withdrawn = group.versions.filter((item) => ['withdrawn', 'archived'].includes(item.status)).length;
        return <Text type="secondary">{withdrawn ? `${withdrawn} 个已撤回` : '无已撤回版本'}</Text>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: () => <Text type="secondary">展开管理版本</Text>,
    },
  ];

  const overrideEvidenceMode = Form.useWatch('evidence_mode', overrideForm);
  const reviewStatusValue = Form.useWatch('review_status', reviewForm);
  const overrideRuns = overrideCandidate ? runsByPackage[overrideCandidate.id] || [] : [];
  const detailCandidate = packages.find((item) => item.id === detailPackageId);
  const detailExpectedModelId = detailCandidate ? manifestModelId(detailCandidate) : undefined;
  const detailExistingModel = detailExpectedModelId ? modelMeta[detailExpectedModelId] : undefined;
  const detailRuns = detailCandidate ? runsByPackage[detailCandidate.id] || [] : [];
  const detailLatestRun = detailRuns[0];
  const detailMeta = detailCandidate?.model_id
    ? modelMeta[detailCandidate.model_id]
    : undefined;
  const detailClinicalEvidence = detailCandidate?.model_id
    ? evidenceByModel[detailCandidate.model_id]
    : undefined;
  const detailPreflight = detailCandidate?.model_id
    ? preflightByModel[detailCandidate.model_id]
    : undefined;
  const detailClinicalSummary = detailClinicalEvidence?.summaries[0];
  const detailClinicalRunId = typeof detailClinicalSummary?.run_id === 'string'
    ? detailClinicalSummary.run_id
    : undefined;
  const detailDraftMissingItems = draftMissingItems(detailMeta);
  const detailIsQualified = isQualifiedStatus(detailCandidate?.status);
  const detailIsRunning = detailCandidate?.status === 'qualification_running';
  const detailCurrentRun = detailLatestRun && detailMeta && !detailMeta.draft_mode
    && (detailIsQualified || detailIsRunning || detailCandidate?.status === detailLatestRun.status)
    ? detailLatestRun
    : undefined;
  const detailHasHistoricalRun = Boolean(detailLatestRun && !detailCurrentRun);
  const detailReleases = detailCandidate?.model_id
    ? releasesByModel[detailCandidate.model_id] || []
    : [];
  const detailCurrentReleaseActive = Boolean(
    detailReleases.some((release) => (
      release.package_id === detailCandidate?.id && release.is_active
    )) || (detailCandidate && isCurrentCandidateActive(detailCandidate, detailMeta)),
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {baseLoadNotice ? (
        <Alert
          type="warning"
          showIcon
          closable
          message="部分数据未加载"
          description={baseLoadNotice}
          onClose={() => setBaseLoadNotice(undefined)}
        />
      ) : null}
      <Space wrap>
        <Upload accept=".zip,application/zip" showUploadList={false} beforeUpload={handleUpload}>
          <Button type="primary" icon={<UploadOutlined />}>上传模型包 ZIP</Button>
        </Upload>
        <Button icon={<ReloadOutlined />} onClick={() => void loadBaseData()} loading={loading}>
          刷新
        </Button>
      </Space>

      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={10} xl={9}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            aria-label="搜索模型包"
            placeholder="搜索模型、模型包、版本、药物或作者"
            value={candidateKeyword}
            onChange={(event) => setCandidateKeyword(event.target.value)}
          />
        </Col>
        <Col xs={24} sm={12} md={7} xl={5}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            aria-label="按模型包状态筛选"
            placeholder="全部模型包状态"
            value={packageStatusFilter}
            options={packageStatusOptions}
            style={{ width: '100%' }}
            onChange={setPackageStatusFilter}
          />
        </Col>
        <Col xs={24} sm={12} md={7} xl={5}>
          <Select
            allowClear
            aria-label="按交付阶段筛选"
            placeholder="全部交付阶段"
            value={workflowFilter}
            options={[
              { value: 'registration', label: '待关联/注册' },
              { value: 'qualification', label: '待技术验证' },
              { value: 'review', label: '待版本审核' },
              { value: 'publication', label: '待发布入库' },
              { value: 'activation', label: '待全局测试/切换' },
              { value: 'complete', label: '当前运行版本' },
              { value: 'historical', label: '历史/已撤回' },
            ]}
            style={{ width: '100%' }}
            onChange={setWorkflowFilter}
          />
        </Col>
        <Col xs={24} xl={5}>
          <Space size={8}>
            <Button
              icon={<ReloadOutlined />}
              disabled={!candidateKeyword && !packageStatusFilter && !workflowFilter}
              onClick={() => {
                setCandidateKeyword('');
                setPackageStatusFilter(undefined);
                setWorkflowFilter(undefined);
              }}
            >
              重置
            </Button>
            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
              {groupedPackages.length}/{new Set(packages.map((item) => item.model_id || manifestModelId(item) || item.id)).size}
            </Text>
          </Space>
        </Col>
      </Row>

      <Table<ModelPackageGroup>
        className="compact-status-pagination"
        rowKey="key"
        loading={loading}
        columns={groupedColumns}
        dataSource={groupedPackages}
        scroll={{ x: 1200 }}
        expandable={{
          expandedRowKeys: expandedModelKeys,
          onExpandedRowsChange: (keys) => setExpandedModelKeys(keys as React.Key[]),
          expandedRowRender: (group) => (
            <Table<ModelCandidatePackage>
              rowKey="id"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={group.versions}
              scroll={{ x: 1120 }}
            />
          ),
        }}
        pagination={{
          showTotal: (total) => (
            <Space size={16} wrap>
              <CompactStatusLegend />
              <Text type="secondary">
                {total === groupedPackages.length ? `共 ${total} 个模型` : `筛选 ${total} / 全部 ${groupedPackages.length} 个模型`}
              </Text>
            </Space>
          ),
        }}
      />

      <Modal
        open={Boolean(detailCandidate)}
        title={detailCandidate ? `资格详情 · ${detailCandidate.candidate_id}` : '资格详情'}
        width="min(1280px, 96vw)"
        footer={null}
        centered
        onCancel={() => setDetailPackageId(undefined)}
        destroyOnHidden
        styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingTop: 8 } }}
      >
        {detailCandidate ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <ModelPackageLifecycle
              candidate={detailCandidate}
              meta={detailMeta}
              expectedModelExists={Boolean(detailExistingModel)}
              isLatestCandidate={isLatestCandidate(detailCandidate)}
              activationDecision={detailPreflight?.decision}
              currentReleaseActive={detailCurrentReleaseActive}
              draftMissingItems={detailDraftMissingItems}
              workflow={detailMeta?.workflow}
              loading={lifecycleLoading(detailCandidate)}
              onAction={(action) => void handleLifecycleAction(detailCandidate, action)}
            />
            {rowErrors[detailCandidate.id] ? (
              <Text type="danger">{rowErrors[detailCandidate.id]}</Text>
            ) : null}
            {loadingRuns === detailCandidate.id && !detailLatestRun ? (
              <Spin />
            ) : detailCurrentRun ? (
              <>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="验证器版本">{detailCurrentRun.validator_version}</Descriptions.Item>
                  <Descriptions.Item label="模型包版本">{detailCandidate.package_version}</Descriptions.Item>
                  <Descriptions.Item label="来源文献 SHA-256" span={2}>
                    <Text code>{sourceSha256(detailCandidate)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="脚本产物哈希" span={2}>
                    <Text code>{evidenceText(detailCurrentRun.artifact_hashes)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="运行记录数">{detailRuns.length}</Descriptions.Item>
                  <Descriptions.Item label="当前模型状态">{detailMeta?.model_status || 'inactive'}</Descriptions.Item>
                  <Descriptions.Item label="临床证据状态">
                    <Tag color={['validated', 'external_validated'].includes(detailClinicalEvidence?.validation_status || '') ? 'green' : 'blue'}>
                      {{
                        validated: '已验证',
                        external_validated: '外部验证',
                        pending: '未开展',
                        not_validated: '未形成证据',
                      }[detailClinicalEvidence?.validation_status || 'pending'] || detailClinicalEvidence?.validation_status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="最新临床摘要">
                    {summaryField(detailClinicalSummary, 'id')}
                  </Descriptions.Item>
                </Descriptions>
                {detailClinicalRunId && detailCandidate.model_id && onOpenClinicalResults ? (
                  <Button
                    icon={<BarChartOutlined />}
                    aria-label="View clinical validation results"
                    onClick={() => onOpenClinicalResults(detailCandidate.model_id!, detailClinicalRunId)}
                  >
                    查看临床验证结果
                  </Button>
                ) : null}
                <ModelQualificationStatus
                  qualificationStatus={detailCandidate.status}
                  reviewStatus={detailMeta?.review_status || 'pending'}
                  latestRun={detailCurrentRun}
                  clinicalEvidence={detailClinicalEvidence}
                  activationDecision={detailPreflight?.decision}
                  activationRunId={detailPreflight?.qualification_run_id}
                  publishedQualification={detailMeta?.published_qualification}
                />
              </>
            ) : (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={detailHasHistoricalRun
                    ? `已有 ${detailRuns.length} 条历史运行，仅供追溯；完成登记后重新验证`
                    : detailMeta?.draft_mode ? '完成资料登记后可运行资格验证' : '当前模型尚无有效资格运行记录'}
                />
              </Space>
            )}
            {detailCandidate.model_id ? (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>已发布版本历史</Text>
                <Table<ModelRelease>
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={detailReleases}
                  locale={{ emptyText: '尚无已发布版本' }}
                  columns={[
                    { title: '版本', dataIndex: 'version', width: 120 },
                    {
                      title: '状态',
                      width: 110,
                      render: (_value, release) => release.is_active
                        ? <Tag color="green">当前版本</Tag>
                        : <Tag>{release.status === 'superseded' ? '历史版本' : release.status}</Tag>,
                    },
                    {
                      title: '发布时间',
                      dataIndex: 'published_at',
                      width: 180,
                      render: (value: string | null) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
                    },
                    {
                      title: '脚本指纹',
                      dataIndex: 'script_file_base',
                      ellipsis: true,
                      render: (value: string) => <Text code>{value}</Text>,
                    },
                    {
                      title: '操作',
                      width: 120,
                      render: (_value, release) => (
                        <Button
                          size="small"
                          icon={<RollbackOutlined />}
                          disabled={release.is_active || release.status === 'withdrawn'}
                          loading={switchingReleaseId === release.id}
                          onClick={() => handleSelectRelease(detailCandidate, release)}
                        >
                          切换/回滚
                        </Button>
                      ),
                    },
                  ]}
                />
              </Space>
            ) : null}
          </Space>
        ) : null}
      </Modal>

      <ModelScriptUpgradeModal
        open={Boolean(upgradeCandidate)}
        modelId={upgradeCandidate?.model_id || undefined}
        currentVersion={upgradeCandidate?.package_version}
        onCancel={() => setUpgradeCandidate(undefined)}
        onCompleted={async (result) => {
          await loadBaseData();
          setDetailPackageId(result.id);
          await loadRuns(result.id, true);
        }}
      />

      <Modal
        open={Boolean(renameCandidate)}
        title={renameCandidate?.model_id ? `修改展示名称 · ${renameCandidate.model_id}` : '修改展示名称'}
        okText="保存"
        cancelText="取消"
        confirmLoading={renameSubmitting}
        onCancel={() => setRenameCandidate(undefined)}
        onOk={() => void submitRename()}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          message="仅修改展示名称"
          description="永久技术 ID、历史报告、验证证据和脚本关联均不会改变。"
          style={{ marginBottom: 16 }}
        />
        <Form form={renameForm} layout="vertical" preserve={false}>
          <Form.Item
            name="display_name"
            label="展示名称"
            rules={[
              { required: true, whitespace: true, message: '请输入展示名称' },
              { max: 180, message: '展示名称不能超过 180 个字符' },
            ]}
          >
            <Input placeholder="例如: colin_2019_vancomycin" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={Boolean(reviewCandidate)}
        title={reviewCandidate?.model_id ? `模型审核 · ${reviewCandidate.model_id}` : '模型审核'}
        okText="提交审核"
        cancelText="取消"
        confirmLoading={reviewSubmitting}
        okButtonProps={{ disabled: !reviewQualificationRunId }}
        onCancel={() => {
          setReviewCandidate(undefined);
          setReviewQualificationRunId(undefined);
        }}
        onOk={() => void submitReview()}
        destroyOnHidden
      >
        {!reviewQualificationRunId ? (
          <Alert
            type="info"
            showIcon
            message="正在锁定当前版本的技术资格记录"
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form form={reviewForm} layout="vertical" preserve={false}>
          <Form.Item name="review_status" label="审核结论" rules={[{ required: true }]}>
            <Radio.Group
              options={[
                { label: '审核通过', value: 'approved' },
                { label: '驳回修改', value: 'rejected' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="review_note"
            label="审核意见"
            rules={reviewStatusValue === 'rejected'
              ? [{ required: true, message: '驳回时必须填写审核意见' }]
              : []}
          >
            <Input.TextArea rows={4} placeholder="记录审核依据、需要修改的内容或通过说明" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={Boolean(overrideCandidate)}
        title="审核覆盖激活"
        okText="预检并激活"
        cancelText="取消"
        confirmLoading={overrideSubmitting}
        onCancel={() => setOverrideCandidate(undefined)}
        onOk={() => void submitOverride()}
        destroyOnHidden
      >
        <Form form={overrideForm} layout="vertical" preserve={false}>
          <Form.Item
            name="reason"
            label="覆盖原因"
            rules={[{ required: true, min: 20, message: '请填写至少 20 个字符的审计原因' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="expires_at"
            label="到期时间"
            rules={[{ required: true }]}
          >
            <DatePicker showTime style={{ width: '100%' }} disabledDate={(date) => date.isBefore(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="evidence_mode" label="证据引用" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="run" disabled={!overrideRuns[0]}>引用最新资格运行</Radio>
              <Radio value="no_run">明确说明无运行证据</Radio>
            </Radio.Group>
          </Form.Item>
          {overrideEvidenceMode === 'run' ? (
            <Alert type="info" showIcon message={`资格运行：${overrideRuns[0]?.id || '-'}`} />
          ) : (
            <Form.Item
              name="no_run_statement"
              label="无运行证据说明"
              rules={[{ required: true, message: '请明确说明为何没有可引用的资格运行' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Space>
  );
};

export default CandidatePackagesTab;
